/**
 * ISSUE-964: every per-click league pin in apps/app goes through this hook.
 * One saver per QueryClient is shared, created lazily and kept in a module
 * WeakMap, so the "Your leagues" sheet and the league page star show the
 * same unconfirmed list and never race each other's writes.
 */
import { useFollowsSet, useProfileGet } from "@sportsmet/profile-sdk/private";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { PROFILE_GET_QUERY_KEY } from "../onboarding/profileCacheWriteThrough";
import {
  createLeaguePinSaver,
  type typeLeaguePinSaver,
} from "./leaguePinSaver";

const saversByClient = new WeakMap<QueryClient, typeLeaguePinSaver>();

function getLeaguePinSaver(queryClient: QueryClient): typeLeaguePinSaver {
  const existing = saversByClient.get(queryClient);
  if (existing) {
    return existing;
  }
  const saver = createLeaguePinSaver({
    onConfirmed: async (followedLeagueIds) => {
      await queryClient.cancelQueries({ queryKey: PROFILE_GET_QUERY_KEY });
      queryClient.setQueryData<
        NonNullable<ReturnType<typeof useProfileGet>["data"]>
      >(PROFILE_GET_QUERY_KEY, (old) => {
        if (!old?.data?.profile) {
          return old;
        }
        return {
          ...old,
          data: {
            ...old.data,
            profile: { ...old.data.profile, followedLeagueIds },
          },
        };
      });
    },
    onFailed: (reply) => {
      if (reply !== null) {
        showApiError(reply);
      }
      void queryClient.invalidateQueries({ queryKey: PROFILE_GET_QUERY_KEY });
    },
  });
  saversByClient.set(queryClient, saver);
  return saver;
}

export type typeLeaguePins = {
  /** The profile read itself, for loading and error states. */
  profile: ReturnType<typeof useProfileGet>;
  /** What the screen shows: the unconfirmed list while a save is pending,
   *  otherwise the server list. Never empty-by-accident: [] only when the
   *  fan has no pins or the profile has not loaded. */
  pinnedLeagueIds: string[];
  applyPinChange: (change: (current: string[]) => string[]) => void;
};

export function useLeaguePins(input: { enabled: boolean }): typeLeaguePins {
  const queryClient = useQueryClient();
  const profile = useProfileGet({}, { enabled: input.enabled });
  const follows = useFollowsSet();
  const saver = getLeaguePinSaver(queryClient);
  const pending = useSyncExternalStore(
    saver.subscribe,
    saver.getPendingLeagueIds,
    saver.getPendingLeagueIds,
  );
  const serverLeagueIds = profile.data?.data?.profile?.followedLeagueIds ?? [];
  const followedSportIds = profile.data?.data?.profile?.followedSportIds ?? [];

  const applyPinChange = (change: (current: string[]) => string[]): void => {
    saver.apply({
      serverLeagueIds,
      followedSportIds,
      change,
      send: follows.mutateAsync,
    });
  };

  return {
    profile,
    pinnedLeagueIds: pending ?? serverLeagueIds,
    applyPinChange,
  };
}
